import { useMemo } from 'react';

import { useTaskActivityListQuery } from '@/services/api/taskActivityService';
import { useGetTaskUpdatesQuery } from '@/services/api/tasksService';

import {
  CorrelatedGroupItem,
  isExcludedEvent,
  isFieldChangeEvent,
  isSystemNote,
  isTaskSpecificEvent,
  isUserNote,
  NoteSourceFilter,
  StandaloneEventItem,
  StandaloneUpdateItem,
  TimelineData,
  TimelineFilter,
  TimelineItem,
} from './types';

interface UseTaskTimelineParams {
  matterId: string;
  taskId: string;
  filter: TimelineFilter;
  /** Filter notes by source (user vs system). Defaults to 'all'. */
  sourceFilter?: NoteSourceFilter;
}

/**
 * Hook that fetches and merges TaskUpdates with TaskActivity (domain events)
 * into a unified, filtered timeline.
 *
 * This hook handles the complex logic of:
 * 1. Fetching both TaskUpdates (comments) and TaskActivity (domain events)
 * 2. Correlating events with their linked comments via correlation_id
 * 3. Filtering based on the selected tab ('task' vs 'related')
 * 4. Sorting all items chronologically (newest first)
 *
 * Timeline Item Types:
 * - 'correlated': A field change event + its linked reason comment (grouped together)
 * - 'event': A standalone domain event (no linked comment)
 * - 'update': A standalone comment (not linked to any event)
 *
 * Correlation Logic:
 * When a user provides a reason for a field change, both the domain event and
 * the TaskUpdate comment share the same correlation_id. This hook matches them
 * and creates a CorrelatedGroupItem so they render as a single unit.
 *
 * @param filter - 'task' shows task-specific events + comments, 'related' shows other domain events
 * @returns TimelineData with sorted items and loading/error states
 */
export function useTimeline({
  matterId,
  taskId,
  filter,
  sourceFilter = 'all',
}: UseTaskTimelineParams): TimelineData {
  const {
    data: updates = [],
    isLoading: updatesLoading,
    error: updatesError,
  } = useGetTaskUpdatesQuery({ matterId, taskId });

  const {
    data: activityItems = [],
    isLoading: activityLoading,
    error: activityError,
  } = useTaskActivityListQuery({ matterId, taskId });

  const items = useMemo(() => {
    // Apply source filter to updates
    const filteredUpdates = updates.filter((update) => {
      if (sourceFilter === 'all') return true;
      if (sourceFilter === 'comments') return isUserNote(update);
      if (sourceFilter === 'activity') return isSystemNote(update);
      return true;
    });

    // Build a map of correlation_id -> TaskUpdate for quick lookup
    const updatesByCorrelationId = new Map(
      filteredUpdates
        .filter((u) => u.correlation_id)
        .map((u) => [u.correlation_id, u] as const),
    );

    // Track which updates have been correlated (to avoid duplicates)
    const correlatedUpdateIds = new Set<string>();

    // Track which correlation_ids we've already processed
    const processedCorrelationIds = new Set<string>();

    // Filter and process activity items (domain events)
    const eventItems = activityItems
      .filter((activityItem) => {
        const { event } = activityItem;

        // Exclude task_update_created/updated events (avoid duplicating comments)
        if (isExcludedEvent(event.event_code)) {
          return false;
        }

        // Filter based on tab
        if (filter === 'task') {
          return isTaskSpecificEvent(event.event_code);
        }
        return !isTaskSpecificEvent(event.event_code);
      })
      .map((activityItem) => {
        const { event } = activityItem;
        const correlationId = event.correlation_id;
        const timestamp = event.occurred_at || new Date().toISOString();

        // Check if this event can be correlated with an update (only for task filter)
        if (
          filter === 'task' &&
          correlationId &&
          isFieldChangeEvent(event.event_code) &&
          updatesByCorrelationId.has(correlationId) &&
          !processedCorrelationIds.has(correlationId)
        ) {
          // Create a correlated group
          const linkedUpdate = updatesByCorrelationId.get(correlationId);
          if (linkedUpdate) {
            correlatedUpdateIds.add(linkedUpdate.id);
            processedCorrelationIds.add(correlationId);

            const correlatedItem: CorrelatedGroupItem = {
              type: 'correlated',
              id: `correlated-${correlationId}`,
              correlationId,
              timestamp,
              activityItem,
              update: linkedUpdate,
            };
            return correlatedItem;
          }
        }

        // Standalone event - hide when filtering for comments only
        // (standalone events are activity logs, even if user-triggered)
        if (sourceFilter === 'comments') {
          return null;
        }

        const standaloneEvent: StandaloneEventItem = {
          type: 'event',
          id: `event-${event.id}`,
          timestamp,
          activityItem,
        };
        return standaloneEvent;
      })
      .filter(
        (item): item is StandaloneEventItem | CorrelatedGroupItem =>
          item !== null,
      );

    // Add remaining updates that weren't correlated (only for task filter)
    const updateItems: TimelineItem[] =
      filter === 'task'
        ? filteredUpdates
            .filter((update) => !correlatedUpdateIds.has(update.id))
            .map(
              (update): StandaloneUpdateItem => ({
                type: 'update',
                id: `update-${update.id}`,
                timestamp: update.created_at,
                update,
              }),
            )
        : [];

    // Combine and sort by timestamp (newest first)
    const result = [...eventItems, ...updateItems].sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );

    return result;
  }, [updates, activityItems, filter, sourceFilter]);

  const isLoading = updatesLoading || activityLoading;
  const error = updatesError || activityError;

  return {
    items,
    isLoading,
    error: error ?? null,
  };
}
